import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  User,
  Phone,
  TrendingUp,
  X,
} from "lucide-react";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";

import "./calendar.css";

const statusColors = {
  COMPLETED: "#16a34a",
  IN_PROGRESS: "#dc2626",
  NOT_STARTED: "#f59e0b",
};

const ClientOnboardingCalendar = () => {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedEvent, setSelectedEvent] =
    useState(null);

  const fetchCalendarData = async (
    date = new Date()
  ) => {
    try {
      const formattedDate =
        date.toISOString().split("T")[0];

      const response = await api({
        ...SummaryApi.getOnboardingCalendar,
        url: `${SummaryApi.getOnboardingCalendar.url}?date=${formattedDate}`,
      });

      if (response.data.success) {
        setSummary(response.data.summary);

        const calendarEvents =
          response.data.events.map(
            (item) => ({
              id: item.onboardingId,
              title:
                item.client?.clientName ||
                "Unknown Client",
              start: item.start,
              allDay: true,
              backgroundColor:
                statusColors[
                  item.onboardingStatus
                ],
              borderColor:
                statusColors[
                  item.onboardingStatus
                ],
              extendedProps: item,
            })
          );

        setEvents(calendarEvents);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, []);

  const handleDatesSet = (
    dateInfo
  ) => {
    fetchCalendarData(
      dateInfo.view.currentStart
    );
  };

  const handleEventClick = (
    clickInfo
  ) => {
    setSelectedEvent(
      clickInfo.event.extendedProps
    );
  };

  return (
    <div className="calendar-page">

      {/* Header */}

      <div className="calendar-header">
        <h1>
          Client Onboarding Calendar
        </h1>
        <p>
          Monthly onboarding
          management
        </p>
      </div>

      {/* Summary */}

      {summary && (
        <div className="summary-grid">

          <div className="summary-card total">
            <h4>Total</h4>
            <h2>{summary.total}</h2>
          </div>

          <div className="summary-card completed">
            <h4>Completed</h4>
            <h2>
              {summary.completed}
            </h2>
          </div>

          <div className="summary-card progress">
            <h4>In Progress</h4>
            <h2>
              {summary.inProgress}
            </h2>
          </div>

          <div className="summary-card pending">
            <h4>Not Started</h4>
            <h2>
              {summary.notStarted}
            </h2>
          </div>

        </div>
      )}

      {/* Calendar */}

      <div className="calendar-wrapper">

        <FullCalendar
          plugins={[
            dayGridPlugin,
            interactionPlugin,
          ]}
          initialView="dayGridMonth"
          events={events}
          eventClick={
            handleEventClick
          }
          datesSet={
            handleDatesSet
          }
          dayMaxEvents={3}
          height="auto"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "",
          }}
        />

      </div>

      {/* Modal */}

      <AnimatePresence>

        {selectedEvent && (
          <motion.div
            className="crm-modal-backdrop"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
          >
            <motion.div
              className="crm-modal"
              initial={{
                scale: 0.9,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              exit={{
                scale: 0.9,
                opacity: 0,
              }}
            >
              <button
                className="close-btn"
                onClick={() =>
                  setSelectedEvent(
                    null
                  )
                }
              >
                <X size={18} />
              </button>

              <div className="modal-title">
                {
                  selectedEvent.client
                    ?.clientName
                }
              </div>

              <div className="modal-status">
                {
                  selectedEvent.onboardingStatus
                }
              </div>

              <div className="modal-grid">

                <div>
                  <User size={18} />
                  <span>
                    {
                      selectedEvent.client
                        ?.contactName
                    }
                  </span>
                </div>

                <div>
                  <Phone size={18} />
                  <span>
                    {
                      selectedEvent.client
                        ?.contactPhone
                    }
                  </span>
                </div>

                <div>
                  <TrendingUp size={18} />
                  <span>
                    ₹
                    {(
                      selectedEvent
                        ?.client
                        ?.estimatedValue ||
                      0
                    ).toLocaleString()}
                  </span>
                </div>

                <div>
                  <Calendar size={18} />
                  <span>
                    {new Date(
                      selectedEvent.start
                    ).toLocaleDateString()}
                  </span>
                </div>

              </div>

              <div className="progress-section">

                <div className="progress-top">
                  <span>
                    Completion
                  </span>

                  <span>
                    {
                      selectedEvent.completionPercentage
                    }
                    %
                  </span>
                </div>

                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${selectedEvent.completionPercentage}%`,
                    }}
                  />
                </div>

              </div>

            </motion.div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default ClientOnboardingCalendar;